import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, List } from "@mui/material";
import useAssetTreeStore from "@stores/client/useAssetTreeStore";
import useAssetStore from "@stores/client/useAssetStore";
import { TreeItem } from "./tree-item";
import { Treeloader } from "./tree-loader";
import { getIdFromPath } from "@utils/client/get-id-from-path";
export const AssetTree = () => {
  const {
    getAssetTreeItem,
    fetchAssetTree,
    assetTree,
    fetching,
    fetchingTreeItems,
    setFetchingTreeItems,
    currentRootId,
  } = useAssetTreeStore();
  const { currentRootId: assetCurrentRootId } = useAssetStore();
  const rootSwitching = String(assetCurrentRootId) !== String(currentRootId);
  const location = useLocation();
  const pathname = location.pathname;
  const navigate = useNavigate();
  const idArray = getIdFromPath(pathname);
  const [notFoundError, setNotFoundError] = useState(false);
  useEffect(() => {
    let cancelled = false;

    const initializeAndFetch = async () => {
      if (!currentRootId) {
        return;
      }

      const treeData = await fetchAssetTree();
      if (cancelled) return;
      if (!treeData) {
        setNotFoundError(true);
      }
    };

    initializeAndFetch();

    return () => {
      cancelled = true;
    };
  }, [currentRootId]);

  useEffect(() => {
    let cancelled = false;

    const fetchItems = async () => {
      setFetchingTreeItems(true);
      try {
        let newPath = idArray[0];
        //fetching all parent nodes to ensure the tree structure is expanded correctly
        for (let i = 1; i < idArray.length - 1; i++) {
          newPath = newPath + "-" + idArray[i];
          await getAssetTreeItem(newPath);
          if (cancelled) return;
        }
        //fetching children for the last node only if it has children
        const parentPath = idArray.slice(0, -1).join("-");
        const currentPath = idArray.join("-");

        //reading the latest itemNodes directly from the store
        //to avoid race condition where the state might not have updated with the latest itemNodes after fetching parent nodes
        const latestItemNodes = useAssetTreeStore.getState().itemNodes;

        if (Array.isArray(latestItemNodes?.[parentPath])) {
          const lastNode = latestItemNodes[parentPath].find(
            (node) => node.path === currentPath,
          );
          if (lastNode?.hasChildren && !cancelled) {
            await getAssetTreeItem(currentPath);
          }
        }
      } catch (error) {
        console.error("Error in fetchItems:", error);
      } finally {
        if (!cancelled) setFetchingTreeItems(false);
      }
    };
    if (assetTree && idArray.length > 0) {
      fetchItems();
    }

    return () => {
      cancelled = true;
    };
  }, [assetTree]);

  // Handle navigation in useEffect to avoid state update during render
  useEffect(() => {
    if (notFoundError) {
      navigate("/404");
    }
  }, [notFoundError, navigate]);

  // Render null if notFoundError is true to prevent rendering a flash of the tree while navigating to /404
  if (notFoundError) {
    return null;
  }
  return (
    <>
      {fetchingTreeItems || fetching || !assetTree || rootSwitching ? (
        <Treeloader />
      ) : (
        <Box data-testid="asset-tree">
          {assetTree.map((item) => {
            return (
              <Box key={item.id}>
                <List component="nav" disablePadding>
                  <TreeItem item={item} level={0} key={item.id} />
                </List>
              </Box>
            );
          })}
        </Box>
      )}
    </>
  );
};
